import QuickLRU from "quick-lru";
import * as zarr from "zarrita";

import "./codecs.ts";

import {
  createIcechunkStore,
  isIcechunkStorePath,
  parseStorePath,
} from "./icechunkStore.ts";
import { getLocalZarrStore, isLocalZarrSource } from "./localZarr.ts";
import type { NetCDFArray, NetCDFGroup, TNetCDFBackend } from "./netCDF.ts";

import {
  ZARR_FORMAT,
  type TDataSource,
  type TDatasetSource,
  type TSources,
  type TZarrFormat,
  type TZarrDggsMetadata,
} from "@/lib/types/GlobeTypes.ts";

type TNetCDFNode = {
  readonly format: typeof ZARR_FORMAT.NETCDF;
};

export class ZarrDataManager {
  private static pendingStore: Promise<
    zarr.Location<zarr.AsyncReadable>
  > | null = null;
  private static fetchStorePath: string | null = null;

  private static netCDFBackend: TNetCDFBackend | null = null;

  private static isNetCDFNode(value: unknown): value is TNetCDFNode {
    return (
      typeof value === "object" &&
      value !== null &&
      "format" in value &&
      value.format === ZARR_FORMAT.NETCDF
    );
  }

  private static getNetCDFBackend() {
    if (!this.netCDFBackend) {
      throw new Error("The NetCDF reader is not initialized.");
    }
    return this.netCDFBackend;
  }

  static registerNetCDFBackend(backend: TNetCDFBackend) {
    this.netCDFBackend = backend;
  }

  private static normalizeStorePath(store: string) {
    return store.replace(/\/+$/, "");
  }

  private static normalizeDatasetPath(dataset: string) {
    return dataset.replace(/^\/+/, "").replace(/\/+$/, "");
  }

  public static async createNewStore(storePath: string, isIcechunk = false) {
    if (isLocalZarrSource(storePath)) {
      const localStore = getLocalZarrStore(storePath);
      if (!localStore) {
        throw new Error("Please select the local Zarr directory again.");
      }
      // This store has no getRange, so range-coalescing doesn't apply, but
      // byte-caching still avoids re-reading the same file (e.g. metadata,
      // or chunks revisited when scrubbing back over time steps).
      const localCache = new QuickLRU<string, Uint8Array | undefined>({
        maxSize: 512,
      });
      return zarr.extendStore(localStore, (s) =>
        zarr.withByteCaching(s, { cache: localCache })
      );
    }

    const parsed = parseStorePath(storePath);
    let store: zarr.AsyncReadable | undefined = undefined;
    if (
      isIcechunk ||
      parsed.backend === "icechunk" ||
      isIcechunkStorePath(storePath)
    ) {
      store = await createIcechunkStore(storePath);
    } else {
      store = new zarr.FetchStore(parsed.url, { useSuffixRequest: true });
    }

    const cache = new QuickLRU<string, Uint8Array | undefined>({
      maxSize: 512,
    });
    const fetchStore = zarr.extendStore(
      store,
      (s) => zarr.withRangeCoalescing(s, { coalesceSize: 32768 }),
      (s) => zarr.withByteCaching(s, { cache: cache })
    );
    return fetchStore;
  }

  private static async getDataset(
    datasource: TDatasetSource,
    format?: TZarrFormat
  ): Promise<zarr.Group<zarr.AsyncReadable> | NetCDFGroup> {
    if (datasource.file || format === ZARR_FORMAT.NETCDF) {
      if (!datasource.file) {
        throw new Error("The local NetCDF file is no longer available.");
      }
      return await this.getNetCDFBackend().openGroup(
        datasource.file,
        datasource.store,
        datasource.dataset
      );
    }

    const storePath = this.normalizeStorePath(datasource.store);
    if (!this.pendingStore || this.fetchStorePath !== storePath) {
      this.fetchStorePath = storePath;
      this.pendingStore = this.createNewStore(
        storePath,
        format === ZARR_FORMAT.ICECHUNK
      )
        .then((s) => zarr.root(s))
        .catch((e) => {
          // Clear the cache on failure so callers can retry.
          if (this.fetchStorePath === storePath) {
            this.pendingStore = null;
          }
          throw e;
        });
    }
    // Capture locally so a concurrent path switch cannot swap the store under us.
    const root = await this.pendingStore;
    const datasetPath = this.normalizeDatasetPath(datasource.dataset);
    const target = datasetPath ? root.resolve(datasetPath) : root;
    let dataset: zarr.Group<zarr.AsyncReadable>;
    if (format === ZARR_FORMAT.V2) {
      dataset = await zarr.open.v2(target, { kind: "group" });
    } else if (format === ZARR_FORMAT.V3) {
      dataset = await zarr.open.v3(target, { kind: "group" });
    } else {
      dataset = await zarr.open(target, { kind: "group" });
    }
    return dataset;
  }

  private static async getVariable(
    store: zarr.Group<zarr.AsyncReadable> | NetCDFGroup,
    variable: string,
    format?: TZarrFormat
  ): Promise<zarr.Array<zarr.DataType, zarr.AsyncReadable> | NetCDFArray> {
    if (this.isNetCDFNode(store)) {
      return await this.getNetCDFBackend().openArray(store, variable);
    }
    const fetchPromise = (async () => {
      if (format === ZARR_FORMAT.V2) {
        return await zarr.open.v2(store.resolve(variable), { kind: "array" });
      } else if (format === ZARR_FORMAT.V3) {
        return await zarr.open.v3(store.resolve(variable), { kind: "array" });
      }
      return await zarr.open(store.resolve(variable), {
        kind: "array",
      });
    })();
    const array = await fetchPromise;
    return array;
  }

  static async getDatasetGroup(datasource: TDatasetSource) {
    return (await this.getDataset(
      datasource
    )) as zarr.Group<zarr.AsyncReadable>;
  }

  static async getGroup(
    datasource: TDatasetSource,
    groupPath: string,
    format?: TZarrFormat
  ): Promise<zarr.Group<zarr.AsyncReadable>> {
    const dataset = await this.getDataset(datasource, format);
    const normalizedGroupPath = this.normalizeDatasetPath(groupPath);
    if (!normalizedGroupPath) {
      return dataset as zarr.Group<zarr.AsyncReadable>;
    }

    if (this.isNetCDFNode(dataset)) {
      return (await this.getNetCDFBackend().resolveGroup(
        dataset,
        normalizedGroupPath
      )) as unknown as zarr.Group<zarr.AsyncReadable>;
    }

    const target = dataset.resolve(normalizedGroupPath);
    if (format === ZARR_FORMAT.V2) {
      return await zarr.open.v2(target, { kind: "group" });
    } else if (format === ZARR_FORMAT.V3) {
      return await zarr.open.v3(target, { kind: "group" });
    }
    return await zarr.open(target, { kind: "group" });
  }

  static async getVariableInfo(
    datasource: TDatasetSource,
    variable: string,
    format?: TZarrFormat
  ): Promise<zarr.Array<zarr.DataType, zarr.AsyncReadable>> {
    const group = await this.getDataset(datasource, format);
    const array = await this.getVariable(group, variable, format);
    return array as zarr.Array<zarr.DataType, zarr.AsyncReadable>;
  }

  static async getParentGroup(
    datasources: TSources,
    varname: string,
    format?: TZarrFormat
  ): Promise<zarr.Group<zarr.AsyncReadable>> {
    const groupPath = await ZarrDataManager.resolveGroupPath(varname);
    const source = ZarrDataManager.getDatasetSource(datasources, varname);
    return await ZarrDataManager.getGroup(source, groupPath, format);
  }

  static async getVariableInfoByDatasetSources(
    datasource: TSources,
    variable: string
  ): Promise<zarr.Array<zarr.DataType, zarr.AsyncReadable>> {
    const array = await ZarrDataManager.getVariableInfo(
      ZarrDataManager.getDatasetSource(datasource!, variable),
      variable,
      datasource.zarr_format
    );
    return array;
  }

  static async getVariableData(
    datasource: TDatasetSource,
    variable: string,
    selection?: (number | null | zarr.Slice)[]
  ) {
    const array = await this.getVariableInfo(datasource, variable);
    return await this.getVariableDataFromArray(array, selection);
  }

  static getVariableDataFromArray(
    array: zarr.Array<zarr.DataType, zarr.AsyncReadable>,
    selection?: (number | null | zarr.Slice)[],
    options?: zarr.GetOptions
  ) {
    if (this.isNetCDFNode(array)) {
      return this.getNetCDFBackend().getArray(
        array as unknown as NetCDFArray,
        selection
      );
    }
    if (selection && selection.length > 0) {
      return zarr.get(array, selection, options);
    }
    return zarr.get(array, null, options);
  }

  static async getCRSInfo(
    datasource: TSources,
    variable: string
  ): Promise<zarr.Array<zarr.DataType, zarr.AsyncReadable>> {
    const crsVar = await this.findCRSVar(datasource, variable);
    const variableSource = this.getDatasetSource(datasource, variable);
    return await this.getVariableInfo(
      variableSource,
      ZarrDataManager.resolveVariablePath(variable, crsVar),
      datasource.zarr_format
    );
  }

  static async findCRSVar(datasources: TSources, varname: string) {
    const source = this.getDatasetSource(datasources, varname);
    const datavar = await ZarrDataManager.getVariableInfo(
      source,
      varname,
      datasources.zarr_format
    );
    const group = datavar.attrs?.grid_mapping
      ? undefined
      : await ZarrDataManager.getDatasetGroup(source);
    return this.getCRSVariableName(datavar.attrs, group?.attrs);
  }

  static getCRSVariableName(
    variableAttrs: zarr.Attributes,
    groupAttrs: zarr.Attributes = {}
  ) {
    // The variable's CF mapping takes precedence; group metadata is a fallback.
    const mapping = variableAttrs.grid_mapping ?? groupAttrs.grid_mapping;
    if (mapping) {
      return String(mapping).split(":")[0].trim();
    }
    if (
      (variableAttrs.coordinates as string | undefined)
        ?.split(/\s+/)
        .includes("spatial_ref")
    ) {
      return "spatial_ref";
    }
    return "crs";
  }

  static async getDggsMetadata(
    datasources: TSources,
    varname: string
  ): Promise<TZarrDggsMetadata | null> {
    try {
      const group = await this.getParentGroup(datasources, varname);
      const dggs = group.attrs["dggs"];
      if (dggs !== null && dggs !== undefined) {
        return dggs as TZarrDggsMetadata;
      }
    } catch {
      // on all errors assume the dggs metadata could not be found
    }

    return null;
  }

  static getDatasetSource(
    datasources: TSources,
    varname: string
  ): TDatasetSource {
    return datasources.levels[0].datasources[varname];
  }

  static async getDimensionNames(datasources: TSources, varname: string) {
    const source = this.getDatasetSource(datasources, varname) as TDataSource;
    if (source.attrs && source.attrs.dimensionNames) {
      return source.attrs.dimensionNames as string[];
    }

    const datavar = await ZarrDataManager.getVariableInfo(
      ZarrDataManager.getDatasetSource(datasources, varname),
      varname,
      datasources.zarr_format
    );
    return datavar.dimensionNames ?? [];
  }

  static resolveGroupPath(variable: string): string {
    if (!variable.includes("/")) {
      return "/";
    }

    return variable.split("/").slice(0, -1).join("/");
  }

  static resolveVariablePath(
    contextVariable: string,
    variable: string
  ): string {
    if (variable.includes("/") || !contextVariable.includes("/")) {
      return variable;
    }
    const groupPath = contextVariable.split("/").slice(0, -1).join("/");
    return `${groupPath}/${variable}`;
  }

  static invalidateCache() {
    this.pendingStore = null;
    this.fetchStorePath = null;
    void this.netCDFBackend?.invalidateCache();
  }
}
